import { Injectable, Signal, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import {
    ModuleVersion, VAZHIPADU_MODULE, vazhipaduBookingRoute
} from './module-version-menu';

/** sessionStorage cache key - lives alongside CURRENT_USER/language. */
export const MODULE_VERSIONS_STORAGE_KEY = 'MODULE_VERSIONS';

interface ModuleVersionRow {
    module: string;
    version: string;
}

/**
 * Per-temple module version switch (Workstream E phase 2).
 *
 * Backend contract:
 *   GET v2/module-version → { result: [ { module: 'VAZHIPADU', version: 'V2' } ] }
 *   PUT v2/module-version  body { module, version } → { result: 'success' }
 * (temple inferred from the JWT; unset server-side default is V2).
 *
 * Loaded once after login (login-stoda.component triggers `ensureLoaded()`
 * before navigating; the nav menu calls it too so reload/autoLogin paths are
 * covered). The signal starts from the sessionStorage cache so a reload
 * inside a session renders the correct menu before the refetch lands.
 * Default is always 'V2' before/without data.
 */
@Injectable({ providedIn: 'root' })
export class ModuleVersionService {

    private readonly versions = signal<Record<string, ModuleVersion>>(this.readCache());
    private loaded = false;
    private inflight$: Observable<Record<string, ModuleVersion>> | null = null;
    private readonly versionSignals = new Map<string, Signal<ModuleVersion>>();

    constructor(private http: HttpClient) { }

    /** Reactive version of a module - 'V2' before/without any data. */
    version(module: string): Signal<ModuleVersion> {
        let sig = this.versionSignals.get(module);
        if (!sig) {
            sig = computed(() => this.versions()[module] ?? 'V2');
            this.versionSignals.set(module, sig);
        }
        return sig;
    }

    /** Default booking-screen route for the current VAZHIPADU version. */
    bookingRoute(): string {
        return vazhipaduBookingRoute(this.version(VAZHIPADU_MODULE)());
    }

    /**
     * Fetches the versions once per app boot (subsequent calls replay).
     * Never errors - on failure the cached/default values stay in force and
     * the next call retries.
     */
    ensureLoaded(): Observable<Record<string, ModuleVersion>> {
        if (this.loaded) {
            return of(this.versions());
        }
        if (!this.inflight$) {
            this.inflight$ = this.http.get<{ result: ModuleVersionRow[] }>('v2/module-version').pipe(
                map(resp => {
                    const next: Record<string, ModuleVersion> = {};
                    for (const row of resp?.result ?? []) {
                        if (row?.module && (row.version === 'V1' || row.version === 'V2')) {
                            next[row.module] = row.version;
                        }
                    }
                    return next;
                }),
                tap(next => {
                    this.loaded = true;
                    this.versions.set(next);
                    this.writeCache(next);
                }),
                catchError(() => {
                    this.inflight$ = null; // allow a later retry
                    return of(this.versions());
                }),
                shareReplay(1)
            );
        }
        return this.inflight$;
    }

    /**
     * Forces a refetch (used right after a successful login - the same
     * window may re-login, on cloud even into a different temple).
     */
    refresh(): Observable<Record<string, ModuleVersion>> {
        this.loaded = false;
        this.inflight$ = null;
        return this.ensureLoaded();
    }

    /** Persists a switch and updates the signal + cache on success. */
    setVersion(module: string, version: ModuleVersion): Observable<any> {
        return this.http.put<any>('v2/module-version', { module, version }).pipe(
            tap(() => {
                const next = { ...this.versions(), [module]: version };
                this.versions.set(next);
                this.writeCache(next);
            })
        );
    }

    private readCache(): Record<string, ModuleVersion> {
        try {
            const raw = sessionStorage.getItem(MODULE_VERSIONS_STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (parsed && typeof parsed === 'object') {
                const out: Record<string, ModuleVersion> = {};
                for (const key of Object.keys(parsed)) {
                    if (parsed[key] === 'V1' || parsed[key] === 'V2') {
                        out[key] = parsed[key];
                    }
                }
                return out;
            }
        } catch {
            // fall through to defaults
        }
        return {};
    }

    private writeCache(value: Record<string, ModuleVersion>): void {
        try {
            sessionStorage.setItem(MODULE_VERSIONS_STORAGE_KEY, JSON.stringify(value));
        } catch {
            // storage unavailable - signals still hold the values for this session
        }
    }
}
